import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FileModuleModel, FileModuleType } from 'app/models/file_module.model';
import { FileModuleService } from 'app/services/file_module.service';
import { ApiService } from 'app/shared/apis/api.service';
import { S3Service } from 'app/shared/services/s3.service';
import { ToastService } from 'app/shared/services/toast.service';
import { ImageViewerService } from 'app/shared/services/image-viewer.service';
import { UserModel } from 'app/models/user.model';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-module-attachments',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
    templateUrl: './module-attachments.component.html',
})
export class ModuleAttachmentsComponent implements OnChanges {
    @Input() module: FileModuleType;
    @Input() entityId: number;
    @Input() loggedUser: UserModel;
    /** Filtra listagem e define type_file no create (ex.: Documento, AcordoMensal) */
    @Input() typeFile: string = 'Documento';
    /**
     * Quando true, permite selecionar arquivos sem entityId e esconde "Salvar Anexos"
     * (o formulário pai salva tudo junto via saveAttachments).
     */
    @Input() deferSave = false;
    /** Um único arquivo por entidade (substitui o anterior ao selecionar outro). */
    @Input() singleFile = false;
    @Output() attachmentsChanged = new EventEmitter<void>();

    attachments: FileModuleModel[] = [];
    loading = false;
    pendingAttachments: { file: File; type_file: string }[] = [];
    attachmentsToDelete: FileModuleModel[] = [];

    constructor(
        private fileModuleService: FileModuleService,
        private api: ApiService,
        private s3: S3Service,
        private toast: ToastService,
        private imageViewerService: ImageViewerService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['entityId'] || changes['module'] || changes['typeFile']) && this.entityId && this.module) {
            // Em deferSave, não limpar pendentes — o pai acaba de criar o registro e vai enviar
            if (!this.deferSave) {
                this.pendingAttachments = [];
                this.attachmentsToDelete = [];
            }
            this.loadAttachments();
        }
    }

    get hasChanges(): boolean {
        return this.pendingAttachments.length > 0 || this.attachmentsToDelete.length > 0;
    }

    loadAttachments(): void {
        if (!this.entityId || !this.module) return;

        this.loading = true;
        const filter: any = { module: this.module, entity_id: this.entityId, limit: 'all', page: 1 };
        if (this.typeFile) {
            filter.type_file = this.typeFile;
        }
        this.fileModuleService
            .list(filter)
            .pipe(take(1))
            .subscribe({
                next: (resp) => {
                    let items = (resp.items || []).map((item: any) => new FileModuleModel(item));
                    if (this.typeFile) {
                        items = items.filter((item) => (item.type_file || 'Documento') === this.typeFile);
                    }
                    this.attachments = items;
                    this.loading = false;
                },
                error: (error) => {
                    console.error('Erro ao carregar anexos:', error);
                    this.attachments = [];
                    this.loading = false;
                    this.toast.error('Erro ao carregar anexos.');
                },
            });
    }

    getDisplayName(file: FileModuleModel): string {
        if (file.name) return file.name;
        return this.getFileName(file.path);
    }

    getFileName(path: string): string {
        if (!path) return '';
        try {
            const decoded = decodeURIComponent(path);
            const parts = decoded.split('/');
            return parts[parts.length - 1] || decoded;
        } catch {
            const parts = path.split('/');
            return parts[parts.length - 1] || path;
        }
    }

    isImage(file: FileModuleModel): boolean {
        const fileName = this.getDisplayName(file);
        if (!fileName) return false;
        const extension = fileName.toLowerCase().split('.').pop();
        return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
    }

    isPdf(file: FileModuleModel): boolean {
        const fileName = this.getDisplayName(file);
        if (!fileName) return false;
        return fileName.toLowerCase().split('.').pop() === 'pdf';
    }

    getFileIcon(file: FileModuleModel): string {
        if (this.isImage(file)) return 'heroicons_outline:photo';
        if (this.isPdf(file)) return 'heroicons_outline:document-text';
        return 'heroicons_outline:paper-clip';
    }

    onFileClick(file: FileModuleModel, event?: Event): void {
        if (event) {
            const target = event.target as HTMLElement;
            if (target.closest('button')) return;
        }
        if (this.isImage(file) || this.isPdf(file)) {
            this.openViewer(file);
        }
    }

    openViewer(file: FileModuleModel): void {
        if (!file?.path) {
            this.toast.warning('Arquivo não disponível');
            return;
        }
        try {
            if (this.isImage(file)) {
                const allImageUrls = this.attachments.filter((a) => a && this.isImage(a) && a.path).map((a) => a.path);
                const currentIndex = allImageUrls.indexOf(file.path);
                this.imageViewerService.openLightbox(file.path, allImageUrls, currentIndex >= 0 ? currentIndex : 0);
            } else if (this.isPdf(file)) {
                this.imageViewerService.openInNewTab(file.path);
            }
        } catch (error) {
            console.error('Erro ao abrir visualizador:', error);
            this.toast.error('Erro ao abrir arquivo. Tente novamente.');
        }
    }

    async download(file: FileModuleModel): Promise<void> {
        if (this.isImage(file) || this.isPdf(file)) {
            await this.imageViewerService.openInNewTab(file.path);
            return;
        }

        try {
            const normalizedKey = this.normalizeS3Key(file.path);
            const response = await this.api
                .postSub<{ url: string }, { urlOrKey: string }>('storage/download-url', { urlOrKey: normalizedKey })
                .pipe(take(1))
                .toPromise();

            if (!response?.url) throw new Error('Failed to get presigned URL.');

            const fetchResponse = await fetch(response.url, { method: 'GET', credentials: 'omit' });
            if (!fetchResponse.ok) throw new Error(`HTTP error! status: ${fetchResponse.status}`);

            const blob = await fetchResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = this.getDisplayName(file);
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 200);
        } catch (e) {
            console.error('Erro ao baixar arquivo:', e);
            this.toast.error('Erro ao baixar arquivo.');
        }
    }

    onSelectFiles(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files?.length) return;
        const type_file = this.typeFile || 'Documento';
        const files = Array.from(input.files);

        if (this.singleFile) {
            const file = files[0];
            this.pendingAttachments = [{ file, type_file }];
            // 1:1 — marcar anexos existentes para remoção ao salvar
            for (const existing of this.attachments) {
                if (!this.attachmentsToDelete.some((d) => d.file_module_id === existing.file_module_id)) {
                    this.attachmentsToDelete.push(existing);
                }
            }
            this.attachments = [];
            this.toast.info(
                this.deferSave
                    ? 'Arquivo selecionado. Será enviado ao salvar o formulário.'
                    : 'Arquivo selecionado. Clique em Salvar para enviar.',
            );
        } else {
            for (const file of files) {
                this.pendingAttachments.push({ file, type_file });
            }
            this.toast.info(
                this.deferSave
                    ? 'Arquivo(s) adicionados. Serão enviados ao salvar o formulário.'
                    : 'Arquivo(s) adicionados. Clique em Salvar para enviar.',
            );
        }
        input.value = '';
    }

    remove(file: FileModuleModel): void {
        this.attachmentsToDelete.push(file);
        this.attachments = this.attachments.filter((a) => a.file_module_id !== file.file_module_id);
        this.toast.info('Arquivo marcado para exclusão. Clique em Salvar para confirmar.');
    }

    removePending(index: number): void {
        this.pendingAttachments.splice(index, 1);
        // 1:1 — se cancelou o novo arquivo, restaura o anexo existente marcado para exclusão
        if (this.singleFile && !this.pendingAttachments.length && this.attachmentsToDelete.length) {
            this.attachments = [...this.attachmentsToDelete];
            this.attachmentsToDelete = [];
        }
    }

    /**
     * Persiste pendências. `entityIdOverride` permite enviar logo após o create,
     * antes do Angular propagar o novo @Input entityId.
     */
    async saveAttachments(entityIdOverride?: number, options?: { silent?: boolean }): Promise<void> {
        const entityId = entityIdOverride ?? this.entityId;
        if (!entityId || !this.module) {
            this.toast.error('Não foi possível enviar anexos: registro sem identificador.');
            return;
        }
        if (!this.hasChanges) {
            if (!options?.silent && !this.deferSave) {
                this.toast.info('Nenhuma alteração para salvar');
            }
            return;
        }

        this.loading = true;
        try {
            for (const file of this.attachmentsToDelete) {
                try {
                    await this.s3.deleteFile(file.path);
                } catch (error) {
                    console.warn('Falha ao remover do S3 (seguindo com soft-delete):', error);
                }
                await this.fileModuleService.delete(file).pipe(take(1)).toPromise();
            }
            this.attachmentsToDelete = [];

            // 1:1: se ainda houver anexos no servidor e há pending, remove extras
            if (this.singleFile && this.pendingAttachments.length) {
                const existing = await this.fileModuleService
                    .list({
                        module: this.module,
                        entity_id: entityId,
                        type_file: this.typeFile || 'Documento',
                        limit: 'all',
                        page: 1,
                    } as any)
                    .pipe(take(1))
                    .toPromise();
                for (const file of existing?.items || []) {
                    try {
                        await this.s3.deleteFile(file.path);
                    } catch {
                        // segue com soft-delete
                    }
                    await this.fileModuleService.delete(new FileModuleModel(file)).pipe(take(1)).toPromise();
                }
            }

            const toUpload = this.singleFile ? this.pendingAttachments.slice(0, 1) : this.pendingAttachments;
            for (const item of toUpload) {
                const formData = new FormData();
                formData.append('file', item.file);
                formData.append('path', `modules/${this.module.toLowerCase()}/attachments`);
                formData.append('name', item.file.name);
                const resp = await this.api.postSub<{ url: string }, FormData>('storage/upload', formData).pipe(take(1)).toPromise();
                await this.fileModuleService
                    .create({
                        path: resp.url,
                        type_file: item.type_file,
                        module: this.module,
                        entity_id: entityId,
                        user_id: this.loggedUser?.user_id,
                        name: item.file.name,
                    } as any)
                    .pipe(take(1))
                    .toPromise();
            }
            this.pendingAttachments = [];
            this.loadAttachments();
            this.attachmentsChanged.emit();
            if (!options?.silent) {
                this.toast.success(this.singleFile ? 'Anexo atualizado com sucesso' : 'Anexos atualizados com sucesso');
            }
        } catch (error) {
            console.error('Erro ao salvar anexos:', error);
            this.toast.error('Erro ao salvar anexos. Tente novamente.');
            this.loading = false;
            throw error;
        }
    }

    private normalizeS3Key(url: string): string {
        if (!url) return '';

        const cdnPrefix = 'https://cdn.artsys.com/';
        if (url.startsWith(cdnPrefix)) return url.slice(cdnPrefix.length);

        const knownPrefixes = ['https://evobrides.s3.amazonaws.com/', 'https://art-sys.s3.amazonaws.com/'];
        for (const prefix of knownPrefixes) {
            if (url.startsWith(prefix)) return url.slice(prefix.length);
        }

        try {
            const parsed = new URL(url);
            if (parsed.hostname.endsWith('s3.amazonaws.com')) {
                return parsed.pathname.replace(/^\/+/, '');
            }
        } catch {
            // ignore
        }

        return url;
    }
}
